import { AlertWithActionsType } from '@masfront/common-b2c'
import { createSelector, Selector } from 'reselect'

import { CheckoutState } from './checkout.reducer'

import { lineDescription } from '../components/CheckoutItem'

import { selectAddressByType } from '~/address/store/address.selectors'
import { selectAgentForBasket, selectIsOffline } from '~/agent/store/agent.selectors'
import {
  selectAllPromosSelected,
  selectAllBonusesSelected,
} from '~/bundles/store/bundles.selectors'
import { filterFalsyValues, capitalize, findByValue } from '~/core/helpers/helpers'
import { RootState } from '~/core/store/root.reducer'
import { DocType } from '~/customer/model/customer.model'
import {
  selectCustomerValues,
  selectBirthDate,
  selectDocType,
  selectCustomerProvince,
  selectIsCompanyCustomerSegment,
} from '~/customer/store/customer.selectors'
import {
  selectAllLinesIds,
  selectLinePhoneNumber,
  selectOperatorId,
  selectLine,
} from '~/line/store/line.selectors'
import {
  selectDocumentTypes,
  selectNationalities,
  selectSegments,
  selectLanguages,
  selectBillingTypes,
} from '~/masterData/store/masterData.selectors'
import { selectTechnology } from '~/sale/store/sale.selectors'
import { LineTypes } from '~/tariff/model/tariff.model'
import { selectSelectedTariff } from '~/tariff/store/tariff.selectors'

type GetCheckout = Selector<RootState, CheckoutState>

export const getCheckout: GetCheckout = (state) => state.checkout
const getId = (_: RootState, id: string) => id
export const selectCheckout = createSelector(getCheckout, getId, (checkout, id) => {
  return checkout[id]
})

export const selectContractId = createSelector(
  (state: RootState) => selectCheckout(state, LineTypes.MAIN),
  (checkout) => checkout?.saleResponse?.data?.accountInfo?.accountNumber,
)

export const selectMainLineError = createSelector(
  (state: RootState) => selectCheckout(state, LineTypes.MAIN),
  (checkout) => {
    return checkout?.saleResponse?.error || checkout?.saleResponse?.httpStatus
      ? {
          error: checkout?.saleResponse?.error,
          httpStatus: checkout?.saleResponse?.httpStatus,
        }
      : null
  },
)

export const selectMainLineTariffIsSelected = createSelector(
  (state: RootState) => selectSelectedTariff(state, LineTypes.MAIN),
  (mainLineTariff) => {
    return mainLineTariff?.name
  },
)

export const selectCheckoutProcessHasFinished = createSelector(
  selectAllLinesIds,
  getCheckout,
  (allLinesIds, checkout) => {
    const eachLineIdStatus = allLinesIds.map(
      (lineId) => checkout[lineId]?.status === AlertWithActionsType.success,
    )
    return eachLineIdStatus.indexOf(false) === -1
  },
)

export const selectAllLinesInfoAndStatus = createSelector(
  selectAllLinesIds,
  getCheckout,
  (state) => state,
  (allLinesIds, checkout, state) => {
    const AllLinesIdsAndStatus = allLinesIds.map((lineId) => {
      const subscriptions = checkout[lineId]?.saleResponse?.data?.accountInfo?.subscriptions || []
      return {
        id: lineId,
        line: { ...selectLineInfoFromLine(state, lineId) } as lineDescription,
        status: checkout[lineId]?.status,
        orderNumber: subscriptions[0]?.subscriptionId,
      }
    })
    return AllLinesIdsAndStatus
  },
)

export const selectProfile = createSelector(
  selectContractId,
  selectAgentForBasket,
  (contractId, agent) => {
    return {
      ...filterFalsyValues({
        contractId: contractId || '',
        agent,
      }),
      acceptGDPR: false, // temporalily
    }
  },
)

export const selectCustomer = createSelector(
  selectCustomerValues,
  selectIsCompanyCustomerSegment,
  selectBirthDate,
  selectDocType,
  selectDocumentTypes,
  selectNationalities,
  selectSegments,
  selectLanguages,
  selectTechnology,
  (
    { province, name, firstName, lastName, ...customer },
    isCompanyCustomerSegment,
    birthdate,
    docType,
    documentTypes,
    nationalities,
    segments,
    languages,
    technology,
  ) => {
    return filterFalsyValues({
      ...customer,
      gender: !isCompanyCustomerSegment ? customer.gender : '',
      name: capitalize(name),
      firstName: capitalize(firstName),
      lastName: capitalize(lastName),
      birthdate,
      docType: findByValue(documentTypes, docType),
      nationality: findByValue(nationalities, customer.nationality),
      customerSegment: findByValue(segments, customer.customerSegment),
      language: findByValue(languages, customer.language),
      technology,
    })
  },
)

export const selectEnterprise = createSelector(
  selectCustomerValues,
  selectDocType,
  selectDocumentTypes,
  selectNationalities,
  selectSegments,
  selectLanguages,
  (
    {
      customerSegment,
      cif,
      companyName,
      name,
      firstName,
      lastName,
      nationality,
      docId,
      language,
      contactPhone,
      email,
      ...customer
    },
    docType,
    documentTypes,
    nationalities,
    segments,
    languages,
  ) => {
    return filterFalsyValues({
      docType: findByValue(documentTypes, DocType.CIF),
      customerSegment: findByValue(segments, customerSegment),
      docId: cif,
      companyName,
      responsable: {
        name: capitalize(name),
        firstName: capitalize(firstName),
        lastName: capitalize(lastName),
        docType: findByValue(documentTypes, docType),
        docId,
        nationality: findByValue(nationalities, nationality),
        language: findByValue(languages, language),
        contactPhone,
        email,
      },
    })
  },
)

export const selectBilling = createSelector(selectBillingTypes, (billingTypes) => {
  return {
    billingTypeId: billingTypes ? billingTypes[0].id : 0,
  }
})

export const selectSignUpFromLine = createSelector(
  selectLine,
  selectDocumentTypes,
  selectLinePhoneNumber,
  selectOperatorId,
  (line, documentTypes, phoneNumber, operatorId) => {
    const { iccBalance, iccAvailable, ...lineSignUpValues } = line?.values.signUp
    const donorOwner = filterFalsyValues({
      ...lineSignUpValues.donorOwner,
      docType: findByValue(documentTypes, lineSignUpValues.donorOwner?.docType),
    })

    const donorOperator = filterFalsyValues(operatorId)

    return {
      ...filterFalsyValues({
        ...lineSignUpValues,
        donorOwner: Object.keys(donorOwner).length > 0 ? donorOwner : '',
        donorOperator: Object.keys(donorOperator).length > 0 ? donorOperator : '',
        lineNumber: phoneNumber,
      }),
    }
  },
)

export const selectTariffFromLine = createSelector(selectSelectedTariff, (tariff) => {
  return {
    id: tariff?.id,
  }
})

export const selectAllBonusesFromLine = createSelector(selectAllBonusesSelected, (bonuses) => {
  return bonuses.map((bonus) => {
    return {
      id: bonus?.id,
    }
  })
})

export const selectAllPromosFromLine = createSelector(selectAllPromosSelected, (promos) => {
  return promos.map((promos) => {
    return {
      id: promos?.id,
      name: promos?.name,
    }
  })
})

export const selectLineInfoFromLine = createSelector(
  selectSelectedTariff,
  selectLinePhoneNumber,
  (tariff, phoneNumber) => {
    return filterFalsyValues({
      lineTariffName: tariff?.name,
      lineNumber: phoneNumber,
    })
  },
)

export const selectAddressFromLine = createSelector(
  selectAddressByType,
  selectCustomerProvince,
  selectIsOffline,
  (address, customerProvince, isOffline) => {
    const { addressOwner, provinceId, ...checkoutAddress } = address?.data

    return isOffline
      ? { shipping: { provinceId: customerProvince?.id } }
      : {
          shipping: {
            ...checkoutAddress,
            provinceId: provinceId || checkoutAddress?.postalCode?.slice(0, 2),
          },
        }
  },
)

export const selectLineSaleRequest = createSelector(
  selectProfile,
  selectCustomer,
  selectEnterprise,
  selectIsCompanyCustomerSegment,
  selectBilling,
  selectSignUpFromLine,
  selectTariffFromLine,
  selectAllBonusesFromLine,
  selectAllPromosFromLine,
  selectAddressFromLine,
  (
    profile,
    customer,
    enterprise,
    isCompanyCustomerSegment,
    billing,
    signUp,
    tariff,
    bonuses,
    promos,
    address,
  ) => {
    return filterFalsyValues({
      profile,
      customer: !isCompanyCustomerSegment ? customer : '',
      enterprise: isCompanyCustomerSegment ? enterprise : '',
      billing,
      basket: [
        filterFalsyValues({
          signUp,
          tariff,
          address,
          bonuses: bonuses.length ? bonuses : null,
          discounts: promos.length ? promos : null,
        }),
      ],
    })
  },
)

export const selectAllLinesInfoAndRequests = createSelector(
  selectAllLinesIds,
  (state) => state,
  (allLinesId, state) => {
    return allLinesId.reduce((acc: { [key: string]: any }, elem) => {
      acc[elem] = {
        id: elem,
        checkoutItem: {
          line: { ...selectLineInfoFromLine(state, elem) },
          status: {
            type: AlertWithActionsType.info,
            text: 'A la espera de crear el pedido',
          },
        },
        saleRequest: { ...selectLineSaleRequest(state, elem, 'SIM') },
      }
      return acc
    }, {})
  },
)
